import {
  renderMetaTags,
  ResponsiveImageType,
  StructuredTextGraphQlResponse,
  TitleMetaLinkTag,
  useQuerySubscription,
  Image as DatoImage,
} from "react-datocms";
import { GetStaticPaths, GetStaticProps } from "next";
import { ParsedUrlQuery } from "querystring";
import { Api, IPlanObject } from "@/api";
import { Layout, SEO, StartFreeButtons, Container, CredibilityBar } from "@/components";
import {
  PlanComparison,
  PlanComparisonFields,
  planComparisionFields,
} from "@/components/PlanComparison/PlanComparison";
import { PromoBannerBlock, promoBannerFields } from "@/components/PromoBanner/PromoBanner";
import { workflowStepsFields, WorkflowSteps, WorkflowStepsType } from "@/components/WorkflowSteps/WorkflowSteps";
import { DATOCMS_API_TOKEN } from "@/config";
import {
  Accordion,
  AccordionFragmentType,
  metaTagsFragment,
  responsiveImageFragment,
  getLinkFieldAndFragments,
  MODULE_KEYS,
  Carousel,
  carouselFragment,
  CarouselFragmentType,
} from "@/modules";
import { getPlansStaticProp } from "@/modules/select-plan-comparison/SelectPlanComparison";
import { SiteBanner } from "@/modules/site-banner";
import { ArtistStyle, Subscription } from "@/types";
import { PromoBanner } from "@narrative-software/narrative-web-ui";
import {
  ProductFeature,
  ProductFeatures,
  productFeaturesSectionFields,
} from "../../components/ProductFeatures/ProductFeatures";
import { heroFields, Hero, HeroFragmentType } from "../../components/hero/Hero";
import { COMPANY_LOGOS } from "../select/index";

interface IPartnershipPage {
  title: string;
  logo: {
    responsiveImage: ResponsiveImageType;
  };
  slug: string;
  headerCtaHref: string;
  hero: HeroFragmentType;
  seo: TitleMetaLinkTag[];
  productFeaturesSection: {
    title: string;
    productFeatures: ProductFeature[];
  };
  promoBanner: PromoBannerBlock;
  testimonialsTitle: string;
  testimonials: CarouselFragmentType;
  workflowSection: WorkflowStepsType;
  planComparison: PlanComparisonFields;
  faqs: AccordionFragmentType;
  heroStyles: ArtistStyle[];
  plansTitle: string;
  plansDescription: string;
}

interface IPartnership {
  page: IPartnershipPage;
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
  artistStyles: ArtistStyle[];
}

type PartnershipPageProps = {
  preview: boolean;
  plans: IPlanObject[];
  subscription: Subscription<IPartnership>;
};

const PartnershipPage = ({ preview, plans, subscription }: PartnershipPageProps) => {
  const { data: { page: data, banner } = {}, error } = useQuerySubscription(subscription);

  if (error) {
    console.error(error);
  }

  if (!data) return null;

  const {
    logo,
    headerCtaHref,
    seo,
    hero,
    productFeaturesSection,
    promoBanner,
    testimonials,
    workflowSection,
    planComparison,
    faqs,
  } = data;

  return (
    <Layout
      product="select"
      preview={preview}
      siteBanner={banner ? <SiteBanner {...banner} /> : null}
      headerCtaHref={headerCtaHref}
    >
      <SEO title="Narrative | Fast image editing | Photographers">{renderMetaTags(seo)}</SEO>
      <Hero
        {...hero}
        prelude={
          <div className="mx-auto mb-10 max-w-[320px] w-full">
            <DatoImage data={logo?.responsiveImage} objectFit="contain" layout="responsive" />
          </div>
        }
      />
      <ProductFeatures {...productFeaturesSection} />
      {promoBanner?.backgroundImage?.responsiveImage?.src && (
        <PromoBanner
          title={promoBanner.title}
          description={promoBanner.description}
          form={
            <StartFreeButtons
              emailButtonAppearance="rainbow"
              product={promoBanner.product}
              className="max-w-130"
              ctaHref={promoBanner.ctaHref}
            />
          }
          backgroundColour={promoBanner.backgroundColor}
          backgroundImagePath={promoBanner.backgroundImage.responsiveImage.src}
          large
        />
      )}
      <div className="flex-col items-center py-12 px-12 bg-white">
        <Container>
          <CredibilityBar logos={COMPANY_LOGOS} label="Featured in:" />
        </Container>
      </div>
      {testimonials && <Carousel {...testimonials} />}
      <WorkflowSteps {...workflowSection} />
      <PlanComparison stripePlans={plans} {...planComparison} />
      <Accordion {...faqs} />
    </Layout>
  );
};

/**
 * Static paths
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const {
    data: {
      data: { pages },
    },
  } = await Api.datocmsGraphqlRequest<{ pages: IPartnershipPage[] }>({
    query: `
        {
            pages: allPartnershipPages  {
                slug
          }
        }
      `,
  });

  return {
    paths: pages.map(({ slug }) => ({ params: { partnership: slug } })),
    fallback: false,
  };
};

interface Params extends ParsedUrlQuery {
  partnership: string;
}

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<{}, Params> = async ({ preview = false, params }) => {
  const [faqsField, faqsFragment] = getLinkFieldAndFragments([MODULE_KEYS.accordion], "faqs");

  const graphqlRequest = {
    preview,
    variables: {
      slug: params?.partnership,
    },
    query: `
      query($slug: String) {
        page: partnershipPage(filter: { slug: { eq: $slug }}) {
          logo {
            responsiveImage {
              ...responsiveImageFragment
            }
          }
          headerCtaHref
          hero {
            ${heroFields}
          }
          productFeaturesSection {
            ${productFeaturesSectionFields}
          }
          promoBanner {
            ${promoBannerFields}
          }
          testimonialsTitle
          testimonials {
            ...testimonialCarouselFragment
          }
          workflowSection {
            ${workflowStepsFields}
          }
          planComparison {
          ${planComparisionFields}
          }
          ${faqsField}
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
      }
      ${carouselFragment}
      ${metaTagsFragment}
      ${faqsFragment}
      ${responsiveImageFragment}
    `,
  };

  const [{ data }, plans] = await Promise.all([
    Api.datocmsGraphqlRequest<IPartnership>(graphqlRequest),
    getPlansStaticProp(),
  ]);

  if ("errors" in data) {
    console.log(data.errors, graphqlRequest.query);
    return { props: {} };
  }

  const initialData = data.data;

  return {
    props: {
      preview,
      plans,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PartnershipPage;
